/**
 * DataLayer Bootstrap
 * Initializes the analytics dataLayer with complete page and site context
 */

import { initialize_data_layer, push_to_data_layer } from './pushToDataLayer.js';
import { initialize_page_context, build_site_context } from './pageContext.js';
import { ANALYTICS_CONFIG } from './constants.js';

// Track initialization state
let is_initialized = false;

/**
 * Bootstrap analytics system with initial page context
 * Must be called BEFORE GTM loads
 * @param {Object} route - Initial route object (optional)
 * @param {Object} overrides - Site context overrides (optional)
 * @returns {boolean} - True if successfully initialized
 */
export function bootstrap_analytics(route = {}, overrides = {}) {
  // Prevent duplicate initialization
  if (is_initialized) {
    if (ANALYTICS_CONFIG.debug) {
      console.log('[Analytics] Already initialized, skipping bootstrap');
    }
    return false;
  }

  // SSR-safe check
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    // Step 1: Initialize dataLayer
    initialize_data_layer();

    // Step 2: Initialize page context
    const page_context = initialize_page_context(route);

    // Step 3: Build site context
    const site_context = build_site_context(overrides);

    // Step 4: Create initial bootstrap payload
    const bootstrap_payload = {
      event: 'page_context_bootstrap',
      page: page_context,
      site: site_context,
      timestamp: Date.now()
    };

    // Step 5: Push initial payload to dataLayer
    push_to_data_layer(bootstrap_payload);

    // Mark as initialized
    is_initialized = true;

    if (ANALYTICS_CONFIG.debug) {
      console.log('[Analytics] Bootstrap complete:', bootstrap_payload);
    }

    return true;
  } catch (error) {
    if (ANALYTICS_CONFIG.debug) {
      console.error('[Analytics] Bootstrap failed:', error);
    }
    return false;
  }
}

/**
 * Check if analytics is initialized
 * @returns {boolean} - True if initialized
 */
export function is_analytics_initialized() {
  return is_initialized;
}

/**
 * Force reset initialization state (for testing only)
 */
export function reset_initialization_state() {
  is_initialized = false;
}

/**
 * Get initialization status and diagnostics
 * @returns {Object} - Status information
 */
export function get_initialization_status() {
  return {
    initialized: is_initialized,
    has_datalayer: typeof window !== 'undefined' && Array.isArray(window.dataLayer),
    datalayer_size: typeof window !== 'undefined' && window.dataLayer ? window.dataLayer.length : 0,
    debug_mode: ANALYTICS_CONFIG.debug,
    environment: ANALYTICS_CONFIG.site.environment,
    site_brand: ANALYTICS_CONFIG.site.brand
  };
}
